// D.4 Admin Module v1.0 — canonical manifest of the Admin landing hierarchy.
//
// Tree: Admin landing → 3 tabs (Platform / Program / Project Settings) →
//       sections (Users & Access, Reference Data, etc.) →
//       items (Users, Locations, etc.).
//
// Reorganized 2026-05 from a per-table layout to a task-oriented layout.
// Join/link tables and raw logs that previously had their own top-level
// panels are now marked `advanced: true` — hidden unless the user toggles
// "Show advanced" on the Admin landing.
//
// Rendering rules (see .claude/features/admin-module-v1.0/D.4-header-admin-ux-spec.md § 6.5):
//   • Sections are always accordions (collapsed by default; multi-open).
//   • Multi-item sections render items as nested sub-accordions (exclusive-open within a section).
//   • Single-item sections render item content directly inside the section accordion.
//   • Items with mode='live' embed a real working component.
//   • Items with mode='scaffold' render AdminItemPlaceholder.
//   • Items with mode='pending-q2' render AdminItemPlaceholder with the Q2 banner copy.
//
// Permission gating (see utilities.ts hasAnyAdminPermission):
//   • A user with adminSidewideAccess sees every tab, section and item. This
//     matches the production intent that a platform admin has global visibility
//     and keeps the dev experience navigable before SPRINT-001 seeds the granular
//     Action rows. Do not tighten this without coordinating with the permission
//     seed work.

import { ComponentType, ReactNode } from 'react';

import IMS from './ims/IMS';
import FormVisibility from './sections/FormVisibility/FormVisibility';
import QuickActions from './sections/QuickActions/QuickActions';
import SitewideSettings from './sections/SitewideSettings/SitewideSettings';
import UserSettings from './sections/UserSettings/UserSettings';
import ProgramTypes from './sections/DeliverableTypeSettings/ProgramTypes/ProgramTypes';
import { ProgramTypesProviders } from './sections/DeliverableTypeSettings/ProgramTypes/useProgramTypesContext';
import ProjectTypes from './sections/DeliverableTypeSettings/ProjectTypes/ProjectTypes';
import { ProjectTypesProviders } from './sections/DeliverableTypeSettings/ProjectTypes/useProjectTypesContext';

import AdminActionsPanel from './components/panels/AdminActionsPanel';
import AdminLocationsPanel from './components/panels/AdminLocationsPanel';
import AdminContactsPanel from './components/panels/AdminContactsPanel';
import AdminCLINsPanel from './components/panels/AdminCLINsPanel';
import AdminLocationTypesPanel from './components/panels/AdminLocationTypesPanel';
import AdminOrganizationsPanel from './components/panels/AdminOrganizationsPanel';
import AdminOrganizationTypesPanel from './components/panels/AdminOrganizationTypesPanel';
import AdminInvoiceUploadPanel from './components/panels/AdminInvoiceUploadPanel';
import AdminProgramLocationsPanel from './components/panels/AdminProgramLocationsPanel';
import AdminRolePermissionMappingPanel from './components/panels/AdminRolePermissionMappingPanel';
import AdminRolesPanel from './components/panels/AdminRolesPanel';
import AdminProgramWorkflowTypesPanel from './components/panels/AdminProgramWorkflowTypesPanel';
import AdminProjectCLINsPanel from './components/panels/AdminProjectCLINsPanel';
import AdminUserTypesPanel from './components/panels/AdminUserTypesPanel';
import AdminWorkflowStatesPanel from './components/panels/AdminWorkflowStatesPanel';
import AdminWorkflowTaskStatesPanel from './components/panels/AdminWorkflowTaskStatesPanel';
import AdminWorkflowTypesPanel from './components/panels/AdminWorkflowTypesPanel';

// Surviving admin permission gates after the services-0.11.0 ACL revert
// (commit 0daae3bb). All previously-invented `admin-platform-*` and
// `admin-manage-*` keys were dropped — admin CRUD endpoints reuse
// `admin-sitewide-access` instead. `admin-set-workflow-types` survived
// and still gates ProgramWorkflowType assignment.
export type AdminPermissionKey =
  | 'adminSidewideAccess'
  | 'adminFinancialPlans'
  | 'adminIms'
  | 'adminSetDeliverables'
  | 'adminSetWorkflowTypes';

export type AdminItemMode = 'live' | 'scaffold' | 'pending-q2';

export interface AdminItem {
  id: string;
  label: string;
  tableName: string;
  apiPath?: string;
  permissionKey: AdminPermissionKey;
  mode: AdminItemMode;
  sprintRef?: string;
  description?: string;
  // For live items — the component to embed inside the item accordion body.
  component?: ComponentType;
  // For live items that need a context provider wrapper (e.g., ProgramTypes).
  wrapper?: (children: ReactNode) => ReactNode;
  // Used by the pending-q2 placeholder to point at the open question doc.
  pendingReason?: string;
  // When true, the item is hidden unless the admin landing's "Show advanced"
  // toggle is on. Used for join/link tables and raw audit-log tables that
  // shouldn't be front-and-center in a task-oriented admin UI.
  advanced?: boolean;
}

export interface AdminSection {
  id: string;
  label: string;
  // Permission gate for the section as a whole. If the user lacks it but has
  // adminSidewideAccess, the section still renders (superuser override).
  permissionKey: AdminPermissionKey;
  items: AdminItem[];
}

export interface AdminTab {
  id: 'user-access' | 'platform' | 'program' | 'project' | 'advanced';
  label: string;
  path: `/admin/${'user-access' | 'platform' | 'program' | 'project' | 'advanced'}`;
  sections: AdminSection[];
  // Message shown when the tab has no sections to display (e.g., Project
  // Settings is intentionally a placeholder until project-scoped tools land).
  emptyPlaceholder?: string;
}

// ── User Access tab ──────────────────────────────────────────────────────
// Promoted to its own tab so the user-management surface is the front door
// of the Admin area.

const USER_ACCESS_TAB: AdminTab = {
  id: 'user-access',
  label: 'User Access',
  path: '/admin/user-access',
  sections: [
    {
      id: 'users-access',
      label: 'Manage User Permissions',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'users',
          label: 'Users',
          tableName: 'User',
          apiPath: '/api/users/',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: UserSettings,
          description:
            'Canonical user-management surface. Add/remove users, assign roles, ' +
            'grant role-action sets, and scope role assignments across programs ' +
            'and projects.',
        },
      ],
    },
  ],
};

// ── Platform Settings tab ────────────────────────────────────────────────
// Instance-wide admin work. Reorganized around what an admin does, not the
// underlying tables. Join tables and raw logs live under "Advanced".

const PLATFORM_TAB: AdminTab = {
  id: 'platform',
  label: 'Platform Settings',
  path: '/admin/platform',
  sections: [
    {
      id: 'form-visibility',
      label: 'Module Visibility',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'form-visibility',
          label: 'Module Visibility',
          tableName: 'FormVisibility',
          apiPath: '/api/form-visibility/',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: FormVisibility,
          description:
            'Controls which form types (CTP / TR / RIP / TAR / PNR) are visible in the user nav.',
        },
      ],
    },
    // Site Settings moved to Program Settings tab — backing routes are
    // /api/programs/{id}/site-settings/* on services-0.15.1 (DRIFT-008).
    // Platform admins (adminSidewideAccess) still see it via the
    // isAdminGateOpen superuser override.
    //
    // Advanced is now its own top-level tab (see ADVANCED_TAB below),
    // gated on the "Show advanced" toggle in AdminLanding.
  ],
};

// ── Program Settings tab ─────────────────────────────────────────────────
// Per-program operational admin (schedules, plans, deliverable types).

const PROGRAM_TAB: AdminTab = {
  id: 'program',
  label: 'Program Settings',
  path: '/admin/program',
  // Quick Actions pinned to the top; remaining sections in alphabetical order.
  sections: [
    {
      id: 'quick-actions',
      label: 'Quick Actions',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'quick-actions',
          label: 'Quick Actions',
          tableName: '—',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: QuickActions,
          description: 'Shortcuts to common admin tasks (add a schedule).',
        },
      ],
    },
    {
      id: 'orgs-contacts',
      label: 'Contacts',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'contacts',
          label: 'Contacts',
          tableName: 'Contact',
          apiPath: '/api/contacts',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          sprintRef: 'SPRINT-003 F2',
          component: AdminContactsPanel,
          description:
            'Platform-level contact directory (PII: email, phone). Optional Organization FK.',
        },
      ],
    },
    {
      id: 'deliverable-types',
      label: 'Deliverable Types',
      permissionKey: 'adminSetDeliverables',
      items: [
        {
          id: 'program-deliverable-types',
          label: 'Program Deliverable Types',
          tableName: 'ProgramDeliverableType',
          apiPath: '/api/programs/{id}/deliverable-types',
          permissionKey: 'adminSetDeliverables',
          mode: 'live',
          component: ProgramTypes,
          wrapper: (children) => (
            <ProgramTypesProviders>{children}</ProgramTypesProviders>
          ),
          description:
            'Which deliverable types are associated with the active program.',
        },
      ],
    },
    // "Financial Plans" section removed. Financial Planning ("Plans") was
    // pulled from the UI and its API disabled by the BE (#1447 / ATH-1565,
    // PR #1485). The FinancialsPlans UI component tree was deleted once the
    // /api/financials/plans* endpoints dropped out of the regenerated API
    // client — restore from git history if it returns. The master CLIN catalog
    // that shared this section moved to Project Settings → CLINs (now the
    // single home for all CLIN tables).
    {
      id: 'location',
      label: 'Locations',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'locations',
          label: 'Define Locations',
          tableName: 'Location',
          apiPath: '/api/locations',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          sprintRef: 'SPRINT-002 F1 + SPRINT-003 F4',
          component: AdminLocationsPanel,
          description:
            'Location master catalog. Required LocationType FK. Deletes blocked if a ProgramLocation still references the row.',
        },
        {
          id: 'program-locations',
          label: 'Assign Locations',
          tableName: 'ProgramLocation',
          apiPath: '/api/programs/{id}/locations',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminProgramLocationsPanel,
          description:
            'Pattern B join: assign Locations to a Program. Pick the program first, then add or remove Locations.',
        },
      ],
    },
    {
      id: 'schedule',
      label: 'Schedule',
      permissionKey: 'adminIms',
      items: [
        {
          id: 'ims',
          label: 'Master Schedule',
          tableName: 'IMS',
          apiPath: '/api/master-schedule/',
          permissionKey: 'adminIms',
          mode: 'live',
          component: IMS,
          description:
            'Upload the program-level IMS (.mpp) file; ingests and processes all IMS views/tables.',
        },
      ],
    },
    {
      id: 'site-settings',
      label: 'Site Settings',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'sitewide-settings',
          label: 'Site Settings',
          tableName: 'SiteSettings',
          apiPath: '/api/programs/{id}/site-settings',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: SitewideSettings,
          description:
            'Per-program admin email and approved domains. Pick the program first, then update.',
        },
      ],
    },
    {
      id: 'workflow',
      label: 'Workflow',
      permissionKey: 'adminSetWorkflowTypes',
      items: [
        {
          id: 'program-workflow-types',
          label: 'Program Workflow Types',
          tableName: 'ProgramWorkflowType',
          apiPath: '/api/programs/{id}/workflow-types',
          permissionKey: 'adminSetWorkflowTypes',
          mode: 'live',
          component: AdminProgramWorkflowTypesPanel,
          description:
            'Assign Workflow Types to the active program. (Pattern B join from Program → WorkflowType.)',
        },
      ],
    },
  ],
};

// ── Project Settings tab ─────────────────────────────────────────────────

const PROJECT_TAB: AdminTab = {
  id: 'project',
  label: 'Project Settings',
  path: '/admin/project',
  // Sections in alphabetical order.
  sections: [
    {
      id: 'deliverable-types',
      label: 'Deliverable Types',
      permissionKey: 'adminSetDeliverables',
      items: [
        {
          id: 'project-deliverable-types',
          label: 'Project Deliverable Types',
          tableName: 'ProjectDeliverableType',
          apiPath: '/api/projects/{id}/deliverable-types',
          permissionKey: 'adminSetDeliverables',
          mode: 'live',
          component: ProjectTypes,
          wrapper: (children) => (
            <ProjectTypesProviders>{children}</ProjectTypesProviders>
          ),
          description:
            'Which program-deliverable types are associated with each project.',
        },
      ],
    },
    // Single home for every CLIN table. The master CLIN catalog moved here
    // from the (now removed) Program → "Financial Plans" section; the two
    // Deltek CLIN join tables moved up from the Advanced → "Deltek (Pending)"
    // block. The Deltek pair are pending-q2 placeholders (no admin API yet).
    {
      id: 'clins',
      label: 'CLINs',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'master-clins',
          label: 'CLINs',
          tableName: 'CLIN',
          apiPath: '/api/clins',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          sprintRef: 'SPRINT-003 F1',
          component: AdminCLINsPanel,
          description:
            'Master CLIN catalog. Deletes propagate to ProjectCLIN / DeltekProjectCLIN — server enforces FK guard with 409.',
        },
        {
          id: 'project-clins',
          label: 'Project CLINs',
          tableName: 'ProjectCLIN',
          apiPath: '/api/projects/{id}/clins',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminProjectCLINsPanel,
          description:
            'Associate CLINs with Athena Projects (Pattern B join). Cross-program CLIN/Project guards enforced server-side.',
        },
        {
          id: 'deltek-project-clins',
          label: 'DeltekProject CLINs',
          tableName: 'DeltekProjectCLIN',
          permissionKey: 'adminSidewideAccess',
          mode: 'pending-q2',
          pendingReason:
            'Pending Q2 scope decision (see docs/features/Admin-Module-v1.0-Open-Questions.md). The DeltekProjectCLIN join table is populated via Deltek ETL; admin CRUD lands in a future release.',
        },
        {
          id: 'deltek-project-project-clins',
          label: 'DeltekProject Project CLINs',
          tableName: 'DeltekProjectProjectCLIN',
          permissionKey: 'adminSidewideAccess',
          mode: 'pending-q2',
          pendingReason:
            'Pending Q2 scope decision. Three-way join across Athena project, Deltek project, and CLIN.',
        },
      ],
    },
  ],
};

// ── Advanced tab ─────────────────────────────────────────────────────────
// Tab is gated on the "Show advanced" toggle in AdminLanding — when off,
// AdminLanding filters this tab out of the visible tab bar and it cannot
// be the active tab. Sub-sections group the items by domain.

const ADVANCED_TAB: AdminTab = {
  id: 'advanced',
  label: 'Advanced',
  path: '/admin/advanced',
  sections: [
    {
      id: 'roles-permissions',
      label: 'Roles & Permissions',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'user-types',
          label: 'User Types',
          tableName: 'UserType',
          apiPath: '/api/users/types',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminUserTypesPanel,
          description:
            'User Type catalog (Tyto / Federal / Contractor classifications). Distinct from Roles — see the Role–Permission Mapping item below for the role catalog with permission bindings.',
        },
        {
          id: 'roles',
          label: 'Roles',
          tableName: 'Role',
          apiPath: '/api/users/roles',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminRolesPanel,
          description:
            'Read-only catalog of every role in the system with its classification metadata (role type, source, scope) and total permission count.',
        },
        {
          id: 'actions',
          label: 'Permissions',
          tableName: 'Action',
          apiPath: '/api/actions',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminActionsPanel,
          description:
            'Athena permission-action catalog. CRUD shipped in services-0.11.0.',
        },
        {
          id: 'role-permission-mapping',
          label: 'Role–Permission Mapping',
          tableName: 'RoleAction',
          apiPath: '/api/users/roles',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminRolePermissionMappingPanel,
          description:
            'Read-only JSON view of every role and its granted permissions. Copyable / downloadable for audit records and cross-environment diffing.',
        },
      ],
    },
    {
      id: 'workflow-configuration',
      label: 'Workflow Configuration',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'workflow-types',
          label: 'Workflow Types',
          tableName: 'WorkflowType',
          apiPath: '/api/workflows/types',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminWorkflowTypesPanel,
        },
        {
          id: 'workflow-states',
          label: 'Workflow States',
          tableName: 'WorkflowState',
          apiPath: '/api/workflows/states',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminWorkflowStatesPanel,
          description:
            'Renaming a state affects the state-machine transition table.',
        },
        {
          id: 'workflow-task-states',
          label: 'Task States',
          tableName: 'WorkflowTaskState',
          apiPath: '/api/workflows/task-states',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminWorkflowTaskStatesPanel,
        },
      ],
    },
    {
      id: 'reference-types',
      label: 'Reference Types',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'location-types',
          label: 'Location Types',
          tableName: 'LocationType',
          apiPath: '/api/location-types',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminLocationTypesPanel,
          description:
            'Reference table for Locations. Remove requires reassigning dependent Locations first.',
        },
        {
          id: 'organizations',
          label: 'Organizations',
          tableName: 'Organization',
          apiPath: '/api/organizations',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          sprintRef: 'SPRINT-003 F3',
          component: AdminOrganizationsPanel,
          description:
            'Organization master catalog. Required OrganizationType FK.',
        },
        {
          id: 'organization-types',
          label: 'Organization Types',
          tableName: 'OrganizationType',
          apiPath: '/api/organizations/types',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          component: AdminOrganizationTypesPanel,
          description:
            'Reference table for Organizations. Remove requires reassigning dependent Organizations first.',
        },
      ],
    },
    {
      id: 'program-joins',
      label: 'Program Associations',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'object-types',
          label: 'Object Types',
          tableName: 'ObjectType',
          apiPath: '/api/object-types',
          permissionKey: 'adminSidewideAccess',
          mode: 'scaffold',
          sprintRef: 'Backend gap — GET /api/object-types list-all not wired',
          description:
            'POST/PATCH/DELETE for ObjectType shipped in services-0.11.0 but the GET-list endpoint is missing — backend ticket needed before this binds.',
        },
        {
          id: 'program-object-types',
          label: 'Program Object Types',
          tableName: 'ProgramObjectType',
          apiPath: '/api/programs/{id}/object-types',
          permissionKey: 'adminSidewideAccess',
          mode: 'scaffold',
          sprintRef: 'SPRINT-001 GORM + SPRINT-002 F5',
          description:
            'Link table — Program ↔ Object Type. Normally managed via the Program.',
        },
      ],
    },
    // The two Deltek CLIN join tables (DeltekProjectCLIN,
    // DeltekProjectProjectCLIN) moved to Project Settings → CLINs, alongside
    // the master and Project CLIN tables. They remain pending-q2 placeholders.
    {
      id: 'data-imports',
      label: 'Data Imports',
      permissionKey: 'adminSidewideAccess',
      items: [
        {
          id: 'invoice-upload',
          label: 'Invoice Data Upload',
          tableName: 'ETLRunLog',
          apiPath: '/api/billing/imports',
          permissionKey: 'adminSidewideAccess',
          mode: 'live',
          sprintRef: 'SPRINT-004',
          component: AdminInvoiceUploadPanel,
          description:
            'Upload Deltek invoice CSV files to run the billing ETL. Replaces manual deploy of database-scripts/*.psql.',
        },
      ],
    },
  ],
};

export const ADMIN_MANIFEST: AdminTab[] = [
  USER_ACCESS_TAB,
  PLATFORM_TAB,
  PROGRAM_TAB,
  PROJECT_TAB,
  ADVANCED_TAB,
];

export const getAdminTabByPath = (path: string): AdminTab | undefined =>
  ADMIN_MANIFEST.find((t) => t.path === path);
